/**
 * This module contains the logic for managing asset uploads within the editor.
 */
import { showError } from '@components/app-error';
import { makeUploader, UploadState } from 'client/lib/rpx-client';
import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import { showModalForm } from '@components/modal-form';
import { BaseDialog } from '@components/dialog';
import { BtnPreWarning, BtnPrimary } from '@components/buttons';
import { useIntl } from 'shared/intl/use-intl';
import { IcoChevronDown, IcoChevronUp } from '@components/icons';
import { BatchActions, BatchHeading, MultiImagePreviewModal } from './multi-image-preview-modal';
import { useSequentialUploads } from './use-sequential-uploads';
import { editImage } from './review-image';
import { pickFiles } from './pick-files';
import { FilePickerSource, isEditableImage } from './sources';

export type { FilePickerSource } from './sources';

export type FilePickerOpts = {
  sources?: FilePickerSource[];
  accept?: string;
  requireCrop?: boolean;
  cropRatio?: number;
  isPublic?: boolean;
  /** Media-manager folder to upload into. Undefined means root. */
  folderId?: string;
  /**
   * Called after the user picks a file but before the upload starts. Lets a
   * caller intercept — e.g. offer to reuse an already-uploaded copy. Return
   * `reuse` to resolve the picker with the existing file (no upload), `cancel`
   * to abort, or `proceed` to upload as normal. Must not throw.
   */
  beforeUpload?(file: File): Promise<BeforeUploadDecision>;
};

export type BeforeUploadDecision =
  | { action: 'proceed' }
  | { action: 'reuse'; result: FilePickerResult }
  | { action: 'cancel' };

export interface FilePickerResult {
  fileId: UUID;
  filePath: string;
  publicUrl: string;
  url: string;
  type: string;
  name: string;
  size: number;
}

async function runBeforeUpload(file: File, opts: FilePickerOpts) {
  if (!opts.beforeUpload) {
    return;
  }
  try {
    return await opts.beforeUpload(file);
  } catch {
    // A misbehaving hook must never break the picker — fall through to a
    // normal upload.
    return;
  }
}

function showUploader(file: File, opts: FilePickerOpts) {
  return showModalForm<FilePickerResult | undefined>(({ resolve }) => (
    <BaseDialog
      onClose={resolve}
      contentWidth
      blurBg={false}
      compact
      dismissable={false}
      zIndex="z-[70]"
    >
      <section class="p-8 w-2xl max-w-full">
        <UploaderUI
          file={file}
          folderId={opts.folderId}
          isPublic={opts.isPublic}
          onClose={resolve}
          onComplete={resolve}
        />
      </section>
    </BaseDialog>
  ));
}

export async function filepicker(opts: FilePickerOpts = {}): Promise<FilePickerResult | undefined> {
  try {
    const files = await pickFiles({ sources: opts.sources, accept: opts.accept });
    let file = files?.[0];
    if (!file) {
      return;
    }
    if (isEditableImage(file)) {
      file = await editImage(file, { aspectRatio: opts.cropRatio, requireCrop: opts.requireCrop });
      if (!file) {
        return;
      }
    }

    const decision = await runBeforeUpload(file, opts);
    if (decision?.action === 'reuse') {
      return decision.result;
    }
    if (decision?.action === 'cancel') {
      return;
    }
    return await showUploader(file, opts);
  } catch (err) {
    showError({
      error: 'Failed to upload file. Please try again.',
    });
    throw err;
  }
}

function MultiFileUploaderUI({
  files,
  folderId,
  isPublic,
  onComplete,
}: {
  files: File[];
  folderId?: string;
  isPublic?: boolean;
  onComplete(results: FilePickerResult[]): void;
}) {
  const intl = useIntl();
  const batch = useSequentialUploads(files, { folderId, isPublic });
  const successfulCount = batch.successfulResults.length;
  const allUploaded = batch.phase === 'complete' && successfulCount === files.length;

  useEffect(() => {
    void batch.start();
  }, [batch.start]);

  useEffect(() => {
    if (allUploaded) {
      onComplete(batch.successfulResults);
    }
  }, [allUploaded]);

  const cancel = async () => onComplete(await batch.cancel());

  return (
    <div class="flex flex-col gap-4">
      <header>
        <BatchHeading batch={batch} class="font-semibold" />
        <p class="text-sm text-gray-600 mt-1">
          {successfulCount} of {files.length} files uploaded
        </p>
      </header>

      <ul class="divide-y divide-gray-200 border-y border-gray-200 max-h-80 overflow-y-auto">
        {batch.items.map((item) => (
          <li class="py-3 flex items-center gap-3" key={item.id}>
            <div class="min-w-0 grow">
              <div class="truncate text-sm font-medium" title={item.file.name}>
                {item.file.name}
              </div>
              <div class="text-xs text-gray-500 capitalize">
                {item.status === 'uploading' ? `${Math.round(item.progress)}%` : item.status}
              </div>
              {item.error && <div class="text-xs text-red-700 mt-1">{item.error}</div>}
            </div>
            {item.status === 'failed' && (
              <div class="flex gap-2">
                <button
                  type="button"
                  class="text-sm text-indigo-700 hover:underline"
                  onClick={() => batch.retry(item.id)}
                >
                  {intl('Retry')}
                </button>
                <button
                  type="button"
                  class="text-sm text-gray-600 hover:underline"
                  onClick={() => batch.skip(item.id)}
                >
                  {intl('Skip')}
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>

      <footer class="flex justify-end gap-3">
        <BatchActions
          batch={batch}
          stopClass="rounded-full px-4 py-2 text-sm"
          doneClass="rounded-full px-6"
          onStop={cancel}
          onDone={() => onComplete(batch.successfulResults)}
        />
      </footer>
    </div>
  );
}

export async function multiFilePicker(
  opts: FilePickerOpts = {},
): Promise<FilePickerResult[] | undefined> {
  try {
    const files = await pickFiles({ sources: opts.sources, accept: opts.accept, multiple: true });
    if (!files?.length) {
      return;
    }
    const areAllImages = files.every((f) => f.type.startsWith('image/'));
    if (files.length > 1 && areAllImages) {
      return await showModalForm<FilePickerResult[] | undefined>(({ resolve }) => (
        <MultiImagePreviewModal
          files={files}
          onCancel={() => resolve(undefined)}
          onComplete={(results) => resolve(results)}
          isPublic={opts.isPublic}
          folderId={opts.folderId}
        />
      ));
    }

    if (files.length === 1 && isEditableImage(files[0])) {
      const edited = await editImage(files[0]);
      if (!edited) {
        return;
      }
      files.splice(0, 1, edited);
    }

    if (files.length === 1) {
      const decision = await runBeforeUpload(files[0], opts);
      if (decision?.action === 'reuse') {
        return [decision.result];
      }
      if (decision?.action === 'cancel') {
        return [];
      }
    }

    return await showModalForm<FilePickerResult[]>(({ resolve }) => (
      <BaseDialog
        onClose={() => resolve([])}
        contentWidth
        blurBg={false}
        compact
        dismissable={false}
        zIndex="z-[70]"
      >
        <section class="p-8 w-2xl max-w-full">
          <MultiFileUploaderUI
            files={files}
            folderId={opts.folderId}
            isPublic={opts.isPublic}
            onComplete={resolve}
          />
        </section>
      </BaseDialog>
    ));
  } catch (err) {
    showError({
      error: 'Failed to upload files. Please try again.',
    });
    throw err;
  }
}

export function UploaderUI({
  file,
  folderId,
  isPublic,
  onClose,
  onComplete,
}: {
  file: File;
  folderId?: string;
  isPublic?: boolean;
  onClose(): void;
  onComplete(result: FilePickerResult | undefined): void;
}) {
  const [state, setState] = useState<UploadState & { showError?: boolean; error?: Error }>({
    status: 'init',
    progress: 0,
  });
  const intl = useIntl();
  const uploader = useMemo(
    () =>
      makeUploader({
        file,
        folderId,
        isPublic,
        onProgress({ status, progress }) {
          setState((s) => ({ ...s, status, progress }));
        },
      }),
    [file, folderId, isPublic],
  );
  const upload = useCallback(
    async function upload() {
      try {
        setState((s) => ({ ...s, status: 'init', showError: false, error: undefined }));
        const result = await uploader.upload();
        onComplete(result);
      } catch (err: any) {
        setState((s) => ({ ...s, status: 'failed', error: err }));
      }
    },
    [uploader, onComplete],
  );
  useEffect(() => {
    upload();
    return () => uploader.abort();
  }, [uploader, upload]);

  return (
    <div class="flex flex-col gap-2">
      <header class="font-semibold flex justify-between gap-4 grow whitespace-nowrap">
        <span>
          {state.status === 'init' && intl('Initializing...')}
          {state.status === 'uploading' && intl('Uploading...')}
          {state.status === 'finishing' && intl('Finalizing...')}
          {state.status === 'failed' && intl('Upload failed!')}
          {state.status === 'done' && intl('Complete!')}
        </span>
        <span>
          {state.status !== 'failed' && <>{Math.round(state.progress)}%</>}
          {state.status === 'failed' && state.error && (
            <button
              type="button"
              class="text-indigo-600 ml-2 inline-flex gap-2 items-center"
              onClick={() => setState((s) => ({ ...s, showError: !s.showError }))}
            >
              {intl('Show error')} {state.showError ? <IcoChevronUp /> : <IcoChevronDown />}
            </button>
          )}
        </span>
      </header>
      <section class="w-full overflow-hidden text-ellipsis text-gray-600">{file.name}</section>
      {state.status !== 'failed' && (
        <div class="relative h-2 rounded-full overflow-hidden">
          <div
            class={`absolute inset-y-0 left-0 bg-green-400 rounded-full w-full transition-all ${
              state.status === 'done' ? '' : 'animate-pulse'
            }`}
            style={`width: ${state.progress}%`}
          ></div>
        </div>
      )}
      {(state.status === 'init' || state.status === 'uploading') && (
        <footer class="flex justify-end mt-2">
          <BtnPreWarning
            class="rounded-full p-2 px-4 text-sm"
            data-no-autofocus
            onClick={() => onClose()}
          >
            {intl('Cancel upload')}
          </BtnPreWarning>
        </footer>
      )}
      {state.status === 'failed' && (
        <>
          {state.error && state.showError && (
            <div class="text-gray-600 overflow-auto max-h-80">
              <div>{state.error.message}</div>
              <pre>{state.error.stack}</pre>
            </div>
          )}
          <footer class="flex gap-4 items-center mt-4">
            <BtnPrimary class="rounded-full p-2 px-4" onClick={upload}>
              {intl('Retry')}
            </BtnPrimary>
            <button
              type="button"
              class="rounded-full p-2 px-4 hover:bg-gray-100"
              onClick={() => onClose()}
            >
              {intl('Cancel')}
            </button>
          </footer>
        </>
      )}
    </div>
  );
}
